// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * The arithmetic behind Compare Projects → Division Detail.
 *
 * Two jobs, both answering questions a ranked bar chart could not.
 *
 * "Is this number normal?" needs a distribution, not an order — hence
 * divisionSpread, which reports the median and the middle half so a rate can
 * be read against its peers rather than against the largest job.
 *
 * "Which part of it is high?" needs the cost codes under the division, which
 * is where the old rollup stopped. A division total says Div 03 is $472k; it
 * cannot say that building concrete is in line and site concrete is double.
 */

export interface SubTotal {
  id: string;
  code: string;
  name: string;
  total: number;
  costPerSF: number;
  /** Share of this division, 0–1. The honest way to compare across jobs. */
  shareOfDivision: number;
}

export interface DivisionTotal {
  name: string;
  code: string;
  total: number;
  costPerSF: number;
  subs: SubTotal[];
}

function walkItems(rows: any[]): string[] {
  const ids: string[] = [];
  for (const row of rows || []) {
    if (row.type === "item" || (!row.type && row.id)) ids.push(row.id);
    if (row.rows) ids.push(...walkItems(row.rows));
    if (row.children) ids.push(...walkItems(row.children));
  }
  return ids;
}

/** Matches the estimate's own rollup: switched-off rows, voids and alternates
 *  that were never accepted do not count. */
function includedValue(li: any): number {
  if (!li || !li.included) return 0;
  if (li.altType && !(li.altType === "Alt" && li.altStatus === "Accepted")) return 0;
  return (Number(li.qty) || 0) * (Number(li.unitCost) || 0);
}

export function computeDivisionTotals(
  divisions: any[],
  itemState: Record<string, any>,
  buildingArea: number,
): Record<string, DivisionTotal> {
  const result: Record<string, DivisionTotal> = {};

  for (const div of divisions || []) {
    let total = 0;
    const subs: SubTotal[] = [];

    for (const sub of div.subcategories || []) {
      let subTotal = 0;
      for (const id of walkItems(sub.rows || [])) subTotal += includedValue(itemState[id]);
      total += subTotal;
      // A cost code carrying nothing is noise in a drill-down.
      if (subTotal > 0) {
        subs.push({
          id: sub.id || sub.name || "",
          code: sub.costCode || "",
          name: sub.name || sub.costCode || "(unnamed)",
          total: subTotal,
          costPerSF: buildingArea > 0 ? subTotal / buildingArea : 0,
          shareOfDivision: 0,
        });
      }
    }

    if (total > 0) {
      for (const s of subs) s.shareOfDivision = s.total / total;
      subs.sort((a, b) => b.total - a.total);
      result[div.id] = {
        name: div.name || div.id,
        code: div.code || "",
        total,
        costPerSF: buildingArea > 0 ? total / buildingArea : 0,
        subs,
      };
    }
  }

  return result;
}

/** "03-100", "03.100" and "03100" are one code typed three ways. */
export function normalizeCode(code: string): string {
  return String(code || "").replace(/[-.\s_]/g, "").trim().toLowerCase();
}

export function median(nums: number[]): number {
  if (!nums.length) return 0;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export interface Spread {
  min: number;
  max: number;
  median: number;
  /** The middle half. With very few projects this is a hint, not a statistic. */
  p25: number;
  p75: number;
  count: number;
}

/**
 * Median and quartiles by the "median of halves" method: with the handful of
 * projects a firm actually has, interpolated percentiles imply a precision the
 * sample cannot support. An odd-length list includes its median in both halves,
 * which keeps a 3-point spread from collapsing to its endpoints.
 */
export function divisionSpread(values: number[]): Spread {
  const v = values.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (!v.length) return { min: 0, max: 0, median: 0, p25: 0, p75: 0, count: 0 };

  const mid = Math.floor(v.length / 2);
  const lower = v.slice(0, v.length % 2 ? mid + 1 : mid);
  const upper = v.slice(v.length % 2 ? mid : mid);

  return {
    min: v[0],
    max: v[v.length - 1],
    median: median(v),
    p25: median(lower),
    p75: median(upper),
    count: v.length,
  };
}

/** Where one project's rate sits against the rest, in plain words. */
export function readRate(value: number, s: Spread): "high" | "low" | "typical" {
  if (s.count < 3) return "typical";   // nothing to be unusual against yet
  if (value > s.p75) return "high";
  if (value < s.p25) return "low";
  return "typical";
}

// ── Cost codes across projects ───────────────────────────────────────────────

export interface CodeRow {
  /** Stable identity across projects: the normalized code, or the name when a
   *  subcategory has no code at all. */
  key: string;
  code: string;
  name: string;
  /** Project id → that project's figures for this code. Absent means the
   *  project has no such code, which is itself worth seeing. */
  byProject: Record<string, SubTotal>;
  /** Summed across projects, for ordering. */
  combinedTotal: number;
}

/**
 * The union of cost codes under one division across several projects.
 *
 * Projects rarely carry identical code lists: one job breaks concrete into
 * building and site, another has a single line. Intersecting them would hide
 * exactly the difference worth looking at, so this unions them and leaves a
 * gap where a project has no such code.
 *
 * "03-100" and "03100" are the same code typed two ways, so identity is the
 * normalized form rather than the literal string.
 */
export function unionCostCodes(
  projects: Array<{ id: string; divTotals: Record<string, DivisionTotal> }>,
  divId: string,
): CodeRow[] {
  const rows = new Map<string, CodeRow>();

  for (const p of projects) {
    for (const sub of p.divTotals?.[divId]?.subs || []) {
      const key = normalizeCode(sub.code) || sub.name.trim().toLowerCase();
      if (!key) continue;
      let row = rows.get(key);
      if (!row) {
        row = { key, code: sub.code, name: sub.name, byProject: {}, combinedTotal: 0 };
        rows.set(key, row);
      }
      // First non-empty label wins, so a project that left the code blank does
      // not erase a name another project supplied.
      if (!row.code && sub.code) row.code = sub.code;
      if (!row.name && sub.name) row.name = sub.name;
      row.byProject[p.id] = sub;
      row.combinedTotal += sub.total;
    }
  }

  return [...rows.values()].sort((a, b) => b.combinedTotal - a.combinedTotal);
}
